import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { ShoppingBag, MessageCircle, User, PlusCircle, Package, LogOut, Menu, X, Heart } from 'lucide-react';

function Header() {
    const { user, logout, isAuthenticated } = useAuth();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);

    useEffect(() => {
        if (!isAuthenticated) {
            setUnreadCount(0);
            return;
        }
        fetch('/api/auth/messages.php?unread=true', { credentials: 'include' })
            .then(res => res.json())
            .then(data => { if (data.success) setUnreadCount(data.count || 0); })
            .catch(() => {});
    }, [isAuthenticated]);

    return (
        <header className="sticky top-0 z-50 backdrop-blur-lg bg-white/90 border-b border-gray-200 shadow-sm">
            <nav className="container mx-auto px-4 py-3">
                <div className="flex justify-between items-center">
                    <Link to="/" className="flex items-center">
                        <span className="text-2xl font-bold">
                            <span className="text-gray-800">Sporty</span>
                            <span className="text-orange-500">Replay</span>
                        </span>
                    </Link>

                    {/* Mobile Menu Button */}
                    <div className="md:hidden">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="text-gray-700 p-2 hover:bg-gray-100 rounded-lg transition-colors"
                        >
                            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-6">
                        <Link
                            to="/products"
                            className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                        >
                            <ShoppingBag size={20} />
                            <span>Produkte</span>
                        </Link>

                        {user ? (
                            <>
                                <Link
                                    to="/my-products"
                                    className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                >
                                    <Package size={20} />
                                    <span>Meine Produkte</span>
                                </Link>

                                <Link
                                    to="/favorites"
                                    className="text-gray-600 hover:text-red-500 transition-colors flex items-center gap-2"
                                >
                                    <Heart size={20} />
                                    <span>Merkliste</span>
                                </Link>

                                <Link
                                    to="/products/create"
                                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 font-semibold"
                                >
                                    <PlusCircle size={20} />
                                    <span>Verkaufen</span>
                                </Link>

                                {/* Nachrichten mit Badge */}
                                <button className="relative p-2 text-gray-600 hover:text-orange-500 transition-colors">
                                    <Link to="/messages" className="flex items-center gap-2">
                                        <MessageCircle className="w-6 h-6" />
                                        {isAuthenticated && unreadCount > 0 && (
                                            <span className="absolute -top-1 -right-1 bg-orange-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                                                {unreadCount}
                                            </span>
                                        )}
                                    </Link>
                                </button>

                                <div className="flex items-center space-x-4 ml-4 border-l border-gray-200 pl-4">
                                    <Link
                                        to="/profile"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                    >
                                        <User size={20} />
                                        <span>Profil</span>
                                    </Link>

                                    <button
                                        onClick={logout}
                                        className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
                                    >
                                        <LogOut size={20} />
                                        <span>Abmelden</span>
                                    </button>
                                </div>
                            </>
                        ) : (
                            <div className="flex items-center space-x-4">
                                <Link
                                    to="/login"
                                    className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                >
                                    <User size={20} />
                                    <span>Anmelden</span>
                                </Link>

                                <Link
                                    to="/register"
                                    className="bg-gradient-to-r from-orange-500 to-orange-600 text-white px-4 py-2 rounded-lg hover:shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 font-semibold"
                                >
                                    <PlusCircle size={20} />
                                    <span>Registrieren</span>
                                </Link>
                            </div>
                        )}
                    </div>
                </div>

                {/* Mobile Menu */}
                {isMobileMenuOpen && (
                    <div className="md:hidden mt-4 py-4 border-t border-gray-200">
                        <div className="flex flex-col space-y-4">
                            <Link
                                to="/products"
                                className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                onClick={() => setIsMobileMenuOpen(false)}
                            >
                                <ShoppingBag size={20} />
                                <span>Produkte</span>
                            </Link>

                            {user ? (
                                <>
                                    <Link
                                        to="/my-products"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <Package size={20} />
                                        <span>Meine Produkte</span>
                                    </Link>

                                    <Link
                                        to="/favorites"
                                        className="text-gray-600 hover:text-red-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <Heart size={20} />
                                        <span>Merkliste</span>
                                    </Link>

                                    <Link
                                        to="/products/create"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <PlusCircle size={20} />
                                        <span>Verkaufen</span>
                                    </Link>

                                    <Link
                                        to="/messages"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <MessageCircle size={20} />
                                        <span>Nachrichten</span>
                                    </Link>

                                    <Link
                                        to="/profile"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <User size={20} />
                                        <span>Profil</span>
                                    </Link>

                                    <button
                                        onClick={() => {
                                            logout();
                                            setIsMobileMenuOpen(false);
                                        }}
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                    >
                                        <LogOut size={20} />
                                        <span>Abmelden</span>
                                    </button>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/login"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <User size={20} />
                                        <span>Anmelden</span>
                                    </Link>

                                    <Link
                                        to="/register"
                                        className="text-gray-600 hover:text-orange-500 transition-colors flex items-center gap-2"
                                        onClick={() => setIsMobileMenuOpen(false)}
                                    >
                                        <PlusCircle size={20} />
                                        <span>Registrieren</span>
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </nav>
        </header>
    );
}

export default Header;
